import boundsData from "../data/world-country-bounds.json";
import { WORLD_COUNTRIES } from "../data/world-countries";

export type CountryBounds = {
  minLng: number;
  minLat: number;
  maxLng: number;
  maxLat: number;
  zoom: number;
  lat: number;
  lng: number;
};

const BOUNDS = boundsData as Record<string, CountryBounds>;

/**
 * Natural Earth country polygons often include overseas fragments (Canaries,
 * Guiana, Aruba, Svalbard outliers, Alaska+Hawaii in one bbox, …). The raw
 * bbox center then lands in the ocean / North Africa. These mainland frames
 * are what country pages should open on.
 */
const MAINLAND_FRAME: Record<string, CountryBounds> = {
  spain: {
    // Península + Balears — exclude Canarias and bad NE fragments near Cyprus
    minLng: -9.5,
    minLat: 35.95,
    maxLng: 4.45,
    maxLat: 43.85,
    zoom: 6.3,
    lat: 40.2,
    lng: -3.7,
  },
  france: {
    minLng: -5.2,
    minLat: 41.3,
    maxLng: 9.6,
    maxLat: 51.15,
    zoom: 5.9,
    lat: 46.6,
    lng: 2.2,
  },
  portugal: {
    // Continental PT — exclude Azores / Madeira
    minLng: -9.55,
    minLat: 36.95,
    maxLng: -6.15,
    maxLat: 42.2,
    zoom: 6.9,
    lat: 39.6,
    lng: -8.0,
  },
  netherlands: {
    // European NL — exclude Caribbean municipalities
    minLng: 3.3,
    minLat: 50.75,
    maxLng: 7.25,
    maxLat: 53.55,
    zoom: 7.6,
    lat: 52.15,
    lng: 5.3,
  },
  norway: {
    // Mainland + near islands — exclude Bouvet / far Antarctic claims
    minLng: 4.5,
    minLat: 57.9,
    maxLng: 31.5,
    maxLat: 71.2,
    zoom: 4.9,
    lat: 64.5,
    lng: 12.0,
  },
  "united-states": {
    // Contiguous US — exclude Alaska/Hawaii from the opening frame
    minLng: -125.0,
    minLat: 24.5,
    maxLng: -66.9,
    maxLat: 49.4,
    zoom: 4.2,
    lat: 39.5,
    lng: -98.35,
  },
  denmark: {
    // Jutland + islands — Greenland/Faroes handled separately if needed
    minLng: 8.05,
    minLat: 54.55,
    maxLng: 15.2,
    maxLat: 57.8,
    zoom: 6.5,
    lat: 56.0,
    lng: 10.0,
  },
};

const WC_CENTER = new Map(
  WORLD_COUNTRIES.map((c) => [c.slug.toLowerCase(), { lat: c.lat, lng: c.lng }])
);

export function getCountryBounds(slug?: string | null): CountryBounds | null {
  if (!slug) return null;
  const key = slug.toLowerCase();
  const base = BOUNDS[key];
  const mainland = MAINLAND_FRAME[key];
  if (!base && !mainland) return null;
  if (mainland) return { ...(base || mainland), ...mainland };
  return base;
}

/** Map framing zoom for a country page (tiny islands zoom in). */
export function getCountryMapZoom(
  slug?: string | null,
  fallback = 5
): number {
  const b = getCountryBounds(slug);
  if (b?.zoom) return b.zoom;
  return fallback;
}

export function getCountryMapCenter(
  slug?: string | null,
  fallback?: { lat: number; lng: number }
): { lat: number; lng: number } | null {
  if (!slug) {
    if (fallback && Number.isFinite(fallback.lat) && Number.isFinite(fallback.lng)) {
      return fallback;
    }
    return null;
  }
  const key = slug.toLowerCase();
  const b = getCountryBounds(key);
  if (b) return { lat: b.lat, lng: b.lng };

  // Prefer curated country pin over overseas-inflated bbox midpoints
  const wc = WC_CENTER.get(key);
  if (wc) return wc;

  if (fallback && Number.isFinite(fallback.lat) && Number.isFinite(fallback.lng)) {
    return fallback;
  }
  return null;
}
